/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component, createContext } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiI18n } from '../i18n';
import { EuiIcon } from '../icon';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiText } from '../text';
import { keys, htmlIdGenerator } from '../../services';
import { EuiInnerText } from '../inner_text';
var EuiTreeViewContext = /*#__PURE__*/createContext('');

function hasAriaLabel(x) {
  return x.hasOwnProperty('aria-label');
}

function getTreeId(propId, contextId, idGenerator) {
  return propId !== null && propId !== void 0 ? propId : contextId === '' ? idGenerator() : contextId;
}

var displayToClassNameMap = {
  default: null,
  compressed: 'euiTreeView--compressed'
};
export class EuiTreeView extends Component {
  treeIdGenerator = htmlIdGenerator('euiTreeView');
  static contextType = EuiTreeViewContext;
  isNested = !!this.context;
  state = {
    openItems: this.props.expandByDefault ? this.props.items.map(({
      id,
      children
    }) => children ? id : null).filter(x => x != null) : this.props.items.map(({
      id,
      children,
      isExpanded
    }) => children && isExpanded ? id : null).filter(x => x != null),
    activeItem: '',
    treeID: getTreeId(this.props.id, this.context, this.treeIdGenerator),
    expandChildNodes: this.props.expandByDefault || false
  };

  componentDidUpdate(prevProps) {
    if (this.props.id !== prevProps.id) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        treeID: getTreeId(this.props.id, this.context, this.treeIdGenerator)
      });
    }
  }

  buttonRef = [];
  setButtonRef = (ref, index) => {
    this.buttonRef[index] = ref;
  };
  handleNodeClick = (node, ignoreCallback = false) => {
    const index = this.state.openItems.indexOf(node.id);
    this.setState({
      expandChildNodes: false
    });
    node.isExpanded = !node.isExpanded;

    if (!ignoreCallback && node.callback !== undefined) {
      node.callback();
    }

    if (this.isNodeOpen(node)) {
      // if the node is part of openItems[] then remove it
      this.setState({
        openItems: this.state.openItems.filter((_, i) => i !== index)
      });
    } else {
      // if the node isn't part of openItems[] then add it
      this.setState(prevState => ({
        openItems: [...prevState.openItems, node.id],
        activeItem: node.id
      }));
    }
  }; // check if the node is included in openItems[]

  isNodeOpen = node => {
    return this.state.openItems.includes(node.id);
  }; // Enable keyboard navigation

  onKeyDown = (event, node) => {
    switch (event.key) {
      case keys.ARROW_DOWN:
        {
          const nodeButtons = Array.from(document.querySelectorAll(`[data-test-subj="euiTreeViewButton-${this.state.treeID}"]`));
          const currentIndex = nodeButtons.indexOf(event.currentTarget);

          if (currentIndex > -1) {
            const nextButton = nodeButtons[currentIndex + 1];

            if (nextButton) {
              event.preventDefault();
              event.stopPropagation();
              nextButton.focus();
            }
          }

          break;
        }

      case keys.ARROW_UP:
        {
          const nodeButtons = Array.from(document.querySelectorAll(`[data-test-subj="euiTreeViewButton-${this.state.treeID}"]`));
          const currentIndex = nodeButtons.indexOf(event.currentTarget);

          if (currentIndex > -1) {
            const prevButton = nodeButtons[currentIndex + -1];

            if (prevButton) {
              event.preventDefault();
              event.stopPropagation();
              prevButton.focus();
            }
          }

          break;
        }

      case keys.ARROW_RIGHT:
        {
          if (!this.isNodeOpen(node)) {
            event.preventDefault();
            event.stopPropagation();
            this.handleNodeClick(node, true);
          }

          break;
        }

      case keys.ARROW_LEFT:
        {
          if (this.isNodeOpen(node)) {
            event.preventDefault();
            event.stopPropagation();
            this.handleNodeClick(node, true);
          }
        }

      default:
        break;
    }
  };
  onChildrenKeydown = (event, index) => {
    if (event.key === keys.ARROW_LEFT) {
      event.preventDefault();
      event.stopPropagation();
      this.buttonRef[index].focus();
    }
  };

  render() {
    const {
      children,
      className,
      items,
      display = 'default',
      expandByDefault,
      showExpansionArrows,
      ...rest
    } = this.props; // Computed classNames

    const classes = classNames('euiTreeView', display ? displayToClassNameMap[display] : null, {
      'euiTreeView--withArrows': showExpansionArrows
    }, className);
    const instructionsId = `${this.state.treeID}--instruction`;
    return <EuiTreeViewContext.Provider value={this.state.treeID}>
        <EuiText size={display === 'compressed' ? 's' : 'm'} className="euiTreeView__wrapper">
          {!this.isNested && <EuiI18n token="euiTreeView.listNavigationInstructions" default="You can quickly navigate this list using arrow keys.">
              {listNavigationInstructions => <EuiScreenReaderOnly>
                  <p id={instructionsId}>{listNavigationInstructions}</p>
                </EuiScreenReaderOnly>}
            </EuiI18n>}
          <ul className={classes} id={!this.isNested ? this.state.treeID : undefined} aria-describedby={!this.isNested ? instructionsId : undefined} {...rest}>
            {items.map((node, index) => {
            const buttonId = node.id;
            const wrappingId = this.treeIdGenerator(buttonId);
            return <EuiInnerText key={node.id + index} fallback={typeof node.label === 'string' ? node.label : ''}>
                  {(ref, innerText) => <EuiI18n key={node.id + index} token="euiTreeView.ariaLabel" default="{nodeLabel} child of {ariaLabel}" values={{
                nodeLabel: innerText,
                ariaLabel: hasAriaLabel(rest) ? rest['aria-label'] : ''
              }}>
                      {ariaLabel => {
                  const label = hasAriaLabel(rest) ? {
                    'aria-label': ariaLabel
                  } : {
                    'aria-labelledby': `${buttonId} ${rest['aria-labelledby']}`
                  };
                  const nodeClasses = classNames('euiTreeView__node', display ? displayToClassNameMap[display] : null, {
                    'euiTreeView__node--expanded': this.isNodeOpen(node)
                  });
                  const nodeButtonClasses = classNames('euiTreeView__nodeInner', showExpansionArrows && node.children ? 'euiTreeView__nodeInner--withArrows' : null, this.state.activeItem === node.id ? 'euiTreeView__node--active' : null, node.className ? node.className : null);
                  return <React.Fragment>
                            <li className={nodeClasses}>
                              <button id={buttonId} aria-controls={wrappingId} aria-expanded={this.isNodeOpen(node)} ref={ref => this.setButtonRef(ref, index)} data-test-subj={`euiTreeViewButton-${this.state.treeID}`} onKeyDown={event => this.onKeyDown(event, node)} onClick={() => this.handleNodeClick(node)} className={nodeButtonClasses}>
                                {showExpansionArrows && node.children ? <EuiIcon className="euiTreeView__expansionArrow" size={display === 'compressed' ? 's' : 'm'} type={this.isNodeOpen(node) ? 'arrowDown' : 'arrowRight'} /> : null}
                                {node.icon && !node.useEmptyIcon ? <span className="euiTreeView__iconWrapper">
                                    {this.isNodeOpen(node) && node.iconWhenExpanded ? node.iconWhenExpanded : node.icon}
                                  </span> : null}
                                {node.useEmptyIcon && !node.icon ? <span className="euiTreeView__iconPlaceholder" /> : null}
                                <span ref={ref} className="euiTreeView__nodeLabel">
                                  {node.label}
                                </span>
                              </button>
                              <div id={wrappingId} onKeyDown={event => this.onChildrenKeydown(event, index)}>
                                {node.children && this.isNodeOpen(node) ? <EuiTreeView items={node.children} display={display} showExpansionArrows={showExpansionArrows} expandByDefault={this.state.expandChildNodes} {...label} /> : null}
                              </div>
                            </li>
                          </React.Fragment>;
                }}
                    </EuiI18n>}
                </EuiInnerText>;
          })}
          </ul>
        </EuiText>
      </EuiTreeViewContext.Provider>;
  }

}
EuiTreeView.propTypes = {
  className: PropTypes.string,
  "data-test-subj": PropTypes.string,

  /** An array of EuiTreeViewNodes
       */
  items: PropTypes.arrayOf(PropTypes.shape({
    /** An array of EuiTreeViewNodes to render as children
       */
    children: PropTypes.arrayOf(PropTypes.any.isRequired),

    /** The readable label for the item
       */
    label: PropTypes.node.isRequired,

    /** A unique ID
       */
    id: PropTypes.string.isRequired,

    /** An icon to use on the left of the label
       */
    icon: PropTypes.element,

    /** Display a different icon when the item is expanded.
      For instance, an open folder or a down arrow
      */
    iconWhenExpanded: PropTypes.element,

    /** Use an empty icon to keep items without an icon
      lined up with their siblings
      */
    useEmptyIcon: PropTypes.bool,

    /** Whether or not the item is expanded.
       */
    isExpanded: PropTypes.bool,

    /** Optional class to throw on the node
       */
    className: PropTypes.string,

    /** Function to call when the item is clicked.
       The open state of the item will always be toggled.
       */
    callback: PropTypes.func
  }).isRequired).isRequired,

  /** Optionally use a variation with smaller text and icon sizes
       */
  display: PropTypes.oneOf(["default", "compressed"]),

  /** Set all items to open on initial load
       */
  expandByDefault: PropTypes.bool,

  /** Display expansion arrows next to all items
       * that contain children
       */
  showExpansionArrows: PropTypes.bool,
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string
};

try {
  EuiTreeView.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/tree_view/tree_view.tsx',
    description: '',
    displayName: 'EuiTreeView',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: 'Never accepts children directly, only through the `items` prop',
        name: 'children',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      display: {
        defaultValue: null,
        description: 'Optionally use a variation with smaller text and icon sizes',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiTreeViewDisplayOptions',
          value: [{
            value: '"default"'
          }, {
            value: '"compressed"'
          }]
        }
      },
      items: {
        defaultValue: null,
        description: 'An array of EuiTreeViewNodes',
        name: 'items',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'Node[]'
        }
      },
      expandByDefault: {
        defaultValue: null,
        description: 'Set all items to open on initial load',
        name: 'expandByDefault',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showExpansionArrows: {
        defaultValue: null,
        description: 'Display expansion arrows next to all items\nthat contain children',
        name: 'showExpansionArrows',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: '',
        name: 'aria-labelledby',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}